import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'
import { DeviceType, Mode, RemoteButtonType, Routes, Theme, UIPart } from '../../../models/enums'
import RemoteButton from './RemoteButton'

interface RemoteProps {
  hideMenu?: boolean
  setTopOfRemote?: (x: number | string) => void
}

const Remote = ({ hideMenu = false, setTopOfRemote }: RemoteProps): JSX.Element => {
  const dispatch = useDispatch()
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const disableHistory = useSelector((state: RootState) => state.retorikReducer.configuration.disableHistory)
  const disableSubtitles = useSelector((state: RootState) => state.retorikReducer.configuration.disableSubtitles)
  const remoteData = useSelector((state: RootState) => state.retorikReducer.configuration.remote)
  const menuData = useSelector((state: RootState) => state.retorikReducer.configuration.menu)
  const baseMenuData = useSelector((state: RootState) => state.retorikReducer.configuration.baseMenu)
  const customMenuData = useSelector((state: RootState) => state.retorikReducer.configuration.customMenu)
  const containerData = useSelector((state: RootState) => state.retorikReducer.configuration.answerpanel)
  const currentDeviceType = useSelector((state: RootState) => state.viewReducer.currentDeviceType)
  const currentHeight = useSelector((state: RootState) => state.viewReducer.currentHeight)
  const isTactile = useSelector((state: RootState) => state.viewReducer.isTactile)
  const theme = useSelector((state: RootState) => state.viewReducer.theme)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const supported = useSelector((state: RootState) => state.localeReducer.supported)
  const disableSound = useSelector((state: RootState) => state.speechReducer.disableSound)

  const [absoluteVerticalTop, setAbsoluteVerticalTop] = useState<string | number | undefined>(undefined)
  const [absoluteVerticalBottom, setAbsoluteVerticalBottom] = useState<string | number | undefined>(undefined)
  const [backgroundColor, setBackgroundColor] = useState<string>()

  useEffect(() => {
    const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize)

    switch (currentDeviceType) {
      // In landscape mode, we set the default position from the bottom
      case DeviceType.landscape:
      case DeviceType.widgetLandscape:
        if (isTactile) {
          if (remoteData?.desktopTactile) {
            setAbsoluteVerticalBottom(undefined)
            setAbsoluteVerticalTop(remoteData.desktopTactile)
          } else if (typeof containerData === 'object' && containerData.landscape?.containerRowStartText) {
            setAbsoluteVerticalBottom((containerData.landscape.containerRowStartText * currentHeight) / 12 - remToPx * 1.5)
            setAbsoluteVerticalTop(undefined)
          } else {
            setAbsoluteVerticalBottom(currentHeight / 6 - remToPx * 1.5)
            setAbsoluteVerticalTop(undefined)
          }
        } else {
          if (remoteData?.desktop) {
            setAbsoluteVerticalBottom(undefined)
            setAbsoluteVerticalTop(remoteData.desktop)
          } else if (typeof containerData === 'object' && containerData.landscape?.containerRowStartText) {
            setAbsoluteVerticalBottom((containerData.landscape.containerRowStartText * currentHeight) / 12 - remToPx * 1.5)
            setAbsoluteVerticalTop(undefined)
          } else {
            setAbsoluteVerticalBottom(currentHeight / 6 - remToPx * 1.5)
            setAbsoluteVerticalTop(undefined)
          }
        }
        break
      // In borne mode, we set the default position from the top
      case DeviceType.borne:
      case DeviceType.widgetBorne:
        let verticalTop: string | number = 0
        if (remoteData?.verticalTactile) {
          verticalTop = remoteData.verticalTactile
        } else if (typeof containerData === 'object' && containerData.borne?.containerRowStart) {
          verticalTop = (containerData.borne.containerRowStart * currentHeight) / 12
        } else {
          verticalTop = (5 * currentHeight) / 12
        }

        setAbsoluteVerticalBottom(undefined)
        setAbsoluteVerticalTop(verticalTop)
        setTopOfRemote?.(verticalTop)

        break
    }
  }, [remoteData, containerData, currentHeight, currentDeviceType, isTactile])

  useEffect(() => {
    setBackgroundColor(theme === Theme.dark ? '#00000099' : '#FFFFFF99')
  }, [theme])

  const handleClick = (e: React.MouseEvent): void => {
    dispatch(storeActions.utils.setClickedOnUI(UIPart.remote))
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <div
      id='retorik-framework-remote'
      className='rf-absolute rf-z-[10] rf-flex rf-flex-col rf-justify-center rf-items-center rf-gap-2 rf-right-6 large-vertical:rf-right-9'
      style={{
        top: absoluteVerticalTop,
        bottom: absoluteVerticalBottom
      }}
      onClick={(e) => handleClick(e)}
    >
      {/* No button in news view, this is only to get a closing button */}
      {route === Routes.News ? (
        <React.Fragment />
      ) : (
        <React.Fragment>
          {/* Home button */}
          <RemoteButton type={RemoteButtonType.home} backgroundColor={backgroundColor} />
          {/* Menu button visible if there are custom menus or more than 1 item in base menu */}
          {!hideMenu && (menuData?.categories?.length || customMenuData?.length || (baseMenuData && baseMenuData.length > 1)) && (
            <RemoteButton type={RemoteButtonType.menu} backgroundColor={backgroundColor} />
          )}
          {/* Language button */}
          {supported.length > 1 && <RemoteButton type={RemoteButtonType.language} backgroundColor={backgroundColor} />}
          {/* Subtitles button */}
          {mode === Mode.vocal && !disableSubtitles && <RemoteButton type={RemoteButtonType.subtitles} backgroundColor={backgroundColor} />}
          {/* Sound button */}
          {!disableSound && <RemoteButton type={RemoteButtonType.sound} backgroundColor={backgroundColor} />}
          {/* History button */}
          {!disableHistory && <RemoteButton type={RemoteButtonType.history} backgroundColor={backgroundColor} />}
        </React.Fragment>
      )}
    </div>
  )
}

export default Remote
